import {
  LoginCredentials,
  OrgSuggestion,
  RegisterOrganizationPayload,
  RegisterOrganizationResponse,
  RegisterPayload,
  TokenData
} from '../model/types';
import { whitelineApi } from '@/shared/api';

interface EmailVerificationErrorResponse {
  data?: unknown;
  headers?: unknown;
  status?: number;
}

interface EmailVerificationRequestError {
  isEmailVerificationRequestError: true;
  name: 'EmailVerificationRequestError';
  response?: EmailVerificationErrorResponse;
}

export type EmailVerifyErrorCode = 'already_confirmed' | 'expired' | 'missing_token' | 'not_found';

export interface EmailVerifyErrorData {
  email_masked: string | null;
  error_code: EmailVerifyErrorCode;
  message: string | null;
}

export interface EmailVerifyErrorDetailsResponse {
  email_masked?: string | null;
  resend_available?: boolean | null;
  trace_id?: string | null;
}

export interface EmailVerifyErrorResponse {
  details?: EmailVerifyErrorDetailsResponse | null;
  error_code: string | null;
  message: string | null;
}

const emailVerifyErrorCodes: EmailVerifyErrorCode[] = [
  'already_confirmed',
  'expired',
  'missing_token',
  'not_found'
];

const getObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  return value as Record<string, unknown>;
};

const sanitizeTokenValue = (value: unknown, token: string): unknown => {
  if (typeof value === 'string') return token ? value.split(token).join('[redacted]') : value;

  if (Array.isArray(value)) return value.map((item) => sanitizeTokenValue(item, token));

  const record = getObjectRecord(value);

  if (!record) return value;

  return Object.entries(record).reduce<Record<string, unknown>>((result, [key, item]) => ({
    ...result,
    [key]: key.toLowerCase().includes('token') ? '[redacted]' : sanitizeTokenValue(item, token)
  }), {});
};

const getEmailVerificationRequestError = (error: unknown, token: string): EmailVerificationRequestError => {
  const errorRecord = getObjectRecord(error);
  const responseRecord = getObjectRecord(errorRecord?.response);
  const status = responseRecord && typeof responseRecord.status === 'number' ? responseRecord.status : undefined;

  return {
    isEmailVerificationRequestError: true,
    name: 'EmailVerificationRequestError',
    ...(responseRecord
      ? {
        response: {
          data: sanitizeTokenValue(responseRecord.data, token),
          headers: sanitizeTokenValue(responseRecord.headers, token),
          status
        }
      }
      : {})
  };
};

const isEmailVerifyErrorCode = (value: unknown): value is EmailVerifyErrorCode => {
  return typeof value === 'string' && emailVerifyErrorCodes.some((errorCode) => errorCode === value);
};

const getEmailVerifyErrorDetailsResponse = (value: unknown): EmailVerifyErrorDetailsResponse | null => {
  const detailsRecord = getObjectRecord(value);

  if (!detailsRecord) return null;

  return {
    ...(detailsRecord.email_masked === null || typeof detailsRecord.email_masked === 'string'
      ? { email_masked: detailsRecord.email_masked }
      : {}),
    ...(detailsRecord.resend_available === null || typeof detailsRecord.resend_available === 'boolean'
      ? { resend_available: detailsRecord.resend_available }
      : {}),
    ...(detailsRecord.trace_id === null || typeof detailsRecord.trace_id === 'string'
      ? { trace_id: detailsRecord.trace_id }
      : {})
  };
};

const getEmailVerifyErrorResponse = (error: unknown): EmailVerifyErrorResponse | null => {
  const errorRecord = getObjectRecord(error);
  const responseRecord = getObjectRecord(errorRecord?.response);

  if (responseRecord?.status !== 400) return null;

  const dataRecord = getObjectRecord(responseRecord.data);

  if (
    !dataRecord
    || (dataRecord.error_code !== null && typeof dataRecord.error_code !== 'string')
    || (dataRecord.message !== null && typeof dataRecord.message !== 'string')
  ) {
    return null;
  }

  return {
    ...(dataRecord.details !== undefined
      ? { details: getEmailVerifyErrorDetailsResponse(dataRecord.details) }
      : {}),
    error_code: dataRecord.error_code,
    message: dataRecord.message
  };
};

export const getEmailVerifyErrorData = (error: unknown): EmailVerifyErrorData | null => {
  const errorResponse = getEmailVerifyErrorResponse(error);

  if (!isEmailVerifyErrorCode(errorResponse?.error_code)) return null;

  return {
    email_masked: errorResponse.details?.email_masked ?? null,
    error_code: errorResponse.error_code,
    message: errorResponse.message
  };
};

export async function loginApi(credentials: LoginCredentials): Promise<TokenData> {
  const response = await whitelineApi.post('/api/auth/authorize', {
    login: credentials.login,
    password: credentials.password
  });
  return { ...response.data, obtained_at: Date.now() };
}

export async function registerApi(payload: RegisterPayload): Promise<TokenData> {
  const response = await whitelineApi.post('/api/register/user', {
    email: payload.email,
    password: payload.password,
    phone: payload.phone,
    utmCampaign: payload.utmCampaign ?? null,
    utmContent: payload.utmContent ?? null,
    utmMedium: payload.utmMedium ?? null,
    utmSource: payload.utmSource ?? null,
    utmTerm: payload.utmTerm ?? null
  });
  return { ...response.data, obtained_at: Date.now() };
}

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  email_masked: string | null;
  outcome: string | null;
}

export const verifyEmail = async (payload: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
  try {
    const response = await whitelineApi.post<VerifyEmailResponse>('/api/email/verify', {
      token: payload.token
    });

    return response.data;
  } catch (error) {
    throw getEmailVerificationRequestError(error, payload.token);
  }
};

export interface ResendVerifyEmailRequest {
  token: string;
}

export interface ResendVerifyEmailResponse {
  email_masked: string | null;
  expires_at: string;
  sent: boolean;
}

export const resendVerifyEmail = async (
  payload: ResendVerifyEmailRequest
): Promise<ResendVerifyEmailResponse> => {
  try {
    const response = await whitelineApi.post<ResendVerifyEmailResponse>('/api/email/verify/resend', {
      token: payload.token
    });

    return response.data;
  } catch (error) {
    throw getEmailVerificationRequestError(error, payload.token);
  }
};

export async function refreshTokenApi(refreshToken: string): Promise<TokenData> {
  const response = await whitelineApi.post('/api/auth/refresh', {
    refresh_token: refreshToken
  });
  return { ...response.data, obtained_at: Date.now() };
}

export async function logoutApi(refreshToken: string): Promise<void> {
  await whitelineApi.post('/api/auth/logout', {
    refresh_token: refreshToken
  });
}

export async function registerOrganizationApi(
  payload: RegisterOrganizationPayload
): Promise<RegisterOrganizationResponse> {
  const response = await whitelineApi.post<RegisterOrganizationResponse & { org_id?: string; id?: string }>(
    '/api/register/client',
    {
      address: payload.address,
      ...(payload.addressFiasId ? { addressFiasId: payload.addressFiasId } : {}),
      fullName: payload.fullName,
      fullNameEn: payload.fullNameEn,
      inn: payload.inn,
      ...(payload.kpp ? { kpp: payload.kpp } : {}),
      ogrn: payload.ogrn,
      phone: payload.phone,
      shortName: payload.shortName
    }
  );

  const orgId = response.data.org_id ?? response.data.id;
  if (!orgId) {
    throw new Error('BFF did not return org_id');
  }

  return { ...response.data, org_id: orgId };
}

export async function getOrganizationSuggestionApi(inn: string): Promise<OrgSuggestion> {
  const trimmed = inn.trim();
  if (!/^\d{10}$|^\d{12}$/.test(trimmed)) {
    throw new Error('ИНН должен содержать 10 или 12 цифр');
  }

  const response = await whitelineApi.get<OrgSuggestion>(`/api/suggestions/${trimmed}`);
  return response.data;
}